import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TrendingUp, Clock, ExternalLink } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import DOMPurify from "dompurify";
import SocialShareButtons from "@/components/SocialShareButtons";
import OpenGraphMeta from "@/components/OpenGraphMeta";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: blogPosts, isLoading } = useBlogPosts();
  const { trackAffiliateClick } = useAffiliateTracking();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  const post = blogPosts?.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const publishDate = new Date(post.publish_date).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const postTypeLabel = post.post_type === 'best_coin_today' 
    ? 'Best Coin Today' 
    : 'Best ROI Token';

  const shareUrl = `${window.location.origin}/post/${post.slug}`;
  const hashtags = post.tokens_mentioned?.map((t: string) => t.replace(/[^a-zA-Z0-9]/g, '')) || ['crypto', 'aussiecrypto'];

  return (
    <div className="min-h-screen bg-background">
      <OpenGraphMeta
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || ''}
        url={shareUrl}
        type="article"
        publishedTime={post.publish_date}
        tags={post.seo_keywords || []}
      />
      {/* Header */}
      <div className="border-b border-border/30 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Picks
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{publishDate}</span>
              <Badge variant="outline" className="text-primary border-primary">
                {postTypeLabel}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Article Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            {post.title}
          </h1>
          
          {post.tokens_mentioned && post.tokens_mentioned.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tokens_mentioned.map((token, idx) => (
                <Badge key={idx} variant="outline" className="border-primary/30">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {token}
                </Badge>
              ))}
            </div>
          )}

          {post.excerpt && (
            <p className="text-xl text-muted-foreground leading-relaxed">
              {post.excerpt}
            </p>
          )}

          {/* Social Share Buttons - Top */}
          <div className="pt-4 border-t border-border/30">
            <SocialShareButtons
              url={shareUrl}
              title={post.title}
              description={post.excerpt || ''}
              hashtags={hashtags}
              showLabel={true}
              size="md"
              articleId={post.id}
              affiliateLinks={post.affiliate_links ? (post.affiliate_links as any[]).map((l: any) => l.url) : []}
            />
          </div>
        </div>

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground"
          dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(post.content, {
              ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'blockquote', 'br', 'span'],
              ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
            })
          }}
        />

        {/* Social Share Buttons - Bottom */}
        <div className="mt-8 pt-6 border-t border-border/30">
          <p className="text-sm text-muted-foreground mb-3">
            Found this useful? Share with your crypto community!
          </p>
          <SocialShareButtons
            url={shareUrl}
            title={post.title}
            description={post.excerpt || ''}
            hashtags={hashtags}
            showLabel={false}
            size="lg"
            articleId={post.id}
            affiliateLinks={post.affiliate_links ? (post.affiliate_links as any[]).map((l: any) => l.url) : []}
          />
        </div>

        {/* Affiliate Links */}
        {post.affiliate_links && Array.isArray(post.affiliate_links) && post.affiliate_links.length > 0 && (
          <div className="mt-12 p-6 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl border border-primary/20">
            <h3 className="text-2xl font-bold mb-4">Buy on Australian Exchanges</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(post.affiliate_links as any[]).map((link: any, idx: number) => (
                <Button
                  key={idx}
                  size="lg"
                  variant={idx === 0 ? "default" : "secondary"}
                  onClick={() => {
                    trackAffiliateClick(link.exchange);
                    window.open(link.url, '_blank');
                  }}
                  className="w-full gap-2"
                >
                  Buy on {link.exchange.toUpperCase()}
                  <ExternalLink className="w-4 h-4" />
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Email Signup CTA */}
        <div className="mt-12 bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-8 text-center text-primary-foreground">
          <h3 className="text-2xl font-bold mb-3">Get Tomorrow's Pick at 6 AM AEST</h3>
          <p className="mb-6 opacity-90">Join 2,500+ Aussies getting daily picks before the market moves</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email"
              className="flex-1 px-4 py-3 rounded-xl bg-white/90 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-white"
            />
            <Button size="lg" className="bg-primary-foreground text-primary hover:bg-white font-bold">
              Subscribe Free
            </Button>
          </div>
        </div>

        {/* More Articles */}
        {blogPosts && blogPosts.length > 1 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">More Daily Insights</h3>
            <div className="grid gap-4">
              {blogPosts
                .filter(p => p.slug !== slug)
                .slice(0, 3)
                .map((relatedPost: any) => (
                  <Link key={relatedPost.id} to={`/post/${relatedPost.slug}`}>
                    <div className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 transition-all">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Badge variant="outline" className="mb-2">
                            {relatedPost.post_type === 'best_coin_today' ? 'Best Coin' : 'Best ROI'}
                          </Badge>
                          <h4 className="font-bold mb-1">{relatedPost.title}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{relatedPost.excerpt}</p>
                        </div>
                        <Button size="sm" variant="ghost">
                          Read →
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Floating Share Button */}
    </div>
  );
}
